/**
 * ActiveGameView — presentational component for the ACTIVE status branch of GameScreen.
 *
 * Covers the three second-level sub-states:
 *   - region-details overlay (RegionDetailsPanel + map)
 *   - isMyTurn:  ClaimUI / ReinforceUI / PostElimPlacementUI / AttackUI / FortifyUI
 *   - !isMyTurn (spectator): map + activity feed
 *
 * Also mounts the HistoricalActiveView time-machine branch on top of the live view.
 *
 * GameScreen owns game state and callbacks; this view owns local rendering
 * concerns such as theme, map registration and layout animations.
 */

import React from 'react'
import { Animated, Easing, View, useWindowDimensions } from 'react-native'
import { useReducedMotion } from 'react-native-reanimated'
import { ClaimUI } from '../../turn/ClaimUI'
import { ReinforceUI } from '../../turn/ReinforceUI'
import { PostElimPlacementUI } from '../../turn/PostElimPlacementUI'
import { AttackUI } from '../../turn/AttackUI'
import { FortifyUI } from '../../turn/FortifyUI'
import { TurnPanelDesktopContext } from '../../turn/TurnControlCard'
import { TURN_PANEL_COLORS } from '../../../lib/turnPanelAppearance'
import { useTheme } from '../../../hooks/useTheme'
import { GameMapHost } from '../../map/GameMapHost'
import {
  updateGameMapSceneRegistration,
  type GameMapSceneConfig,
  type GameMapSceneRegistration,
  type GameMapSceneRegistrationState,
} from '../../map/gameMapScene'
import { RegionDetailsPanel } from '../RegionDetailsPanel'
import type { ActivityFilterState } from '../../../lib/activityFilters'
import { GAME_TOP_BAR_HEIGHT } from '../../../lib/gameChrome'
import { RecentActivitySidebar, recentActivitySidebarWidth } from '../RecentActivitySidebar'
import { HistoricalActiveView } from '../HistoricalActiveView'
import { CardHudButton } from '../../cards/CardHudButton'
import { hasValidSet } from '../../../lib/cards'
import { handForcesTurnIn } from '../../../lib/cardTurnIn'
import type { HydratedGameState } from '../../../lib/gameState'
import type { GameResponse, GamePlayerResponse } from '@warcarp/api-types'
import { isTeamGame } from '@warcarp/api-types'
import type { PanOffset } from '../../../lib/mapZoom'
import type { ActivePhaseView } from '../../../lib/gameScreenState'
import type { PlacementAction } from '../../../lib/placementStack'
import type { PlacementStepMode } from '../../map/PlacementHud'
import { collectDeviceInfo } from '../../../lib/deviceInfo'
import { reportMessage } from '../../../lib/reporting'
import { tutorialHelpCopy, type TutorialHelpCopy } from '../../../lib/tutorialHelp'
import { isTeammateTerritory } from '../../../lib/teamPlay'
import { configureTipExitLayout, TIP_EXIT_FADE_MS } from '../../../lib/tipExit'
import { TutorialHelpStrip } from '../TutorialHelpStrip'
import { WebUpdateStrip } from '../WebUpdateStrip'
import { BootOverdueNotice } from '../BootOverdueNotice'
import { useTutorialTipsCollapsed } from '../../../hooks/useTutorialTipsCollapsed'
import {
  MAP_RENDER_REPORT_COOLDOWN_MS,
  shouldReportMapRender,
  shouldSampleMapRender,
} from '../../../lib/mapPerformance'
export interface ActiveGameViewProps {
  // Core game data
  game: GameResponse
  gameState: HydratedGameState
  myPlayer: GamePlayerResponse | null | undefined
  myPlayerId: string
  isMyTurn: boolean
  slotByPlayerId: Record<string, number>
  playerDisplayName: (playerId: string) => string

  // Phase view selection (computed in container via selectActivePhaseView)
  activePhaseView: ActivePhaseView

  // Pending-placement state, lifted to the container so it survives the regions
  // overlay unmounting the placement UI. Forwarded to ReinforceUI /
  // PostElimPlacementUI (which fall back to local state when unset).
  placementStack: PlacementAction[]
  onPlacementStackChange: React.Dispatch<React.SetStateAction<PlacementAction[]>>
  placementStepMode: PlacementStepMode
  onPlacementStepModeChange: React.Dispatch<React.SetStateAction<PlacementStepMode>>

  // Layout
  isLandscape: boolean
  isDesktop: boolean

  // Historical time-machine
  historicalGameState: HydratedGameState | null
  historicalEventIndex: number | null
  inHistoricalView: boolean
  onScrubEvent: (index: number) => void
  onReturnToLive: () => void
  /** Desktop/web: arrow keys step the snapshot log (see HistoricalActiveView). */
  snapshotArrowKeysEnabled?: boolean
  onViewAtEvent: (index: number) => void
  /** Inline chat input bar (GameChatInputBar): under the map controls bar in
   *  portrait; top of the left column (below its header) on desktop/landscape. */
  chatBar?: React.ReactNode
  /**
   * Your turn with chat open: the chat log that takes over the turn-controls
   * area (the turn UI stays mounted, hidden, so staged moves survive).
   * Landscape passes the column header so the chat bar stays on top.
   */
  /** Your turn: the map bar's log button swaps the turn controls for the log. */
  turnLogOpen?: boolean
  /**
   * Wide layouts (desktop windows, landscape tablets): chat + log are always
   * on screen in a fixed section at the bottom of the side column — under the
   * turn controls on your turn, filling the column otherwise. No chat/log
   * toggles; the chat input is always open.
   */
  persistentChatLog?: boolean
  /** Persistent chat/log: the chat-only filter is on (log shows just chat). */
  chatOnlyLog?: boolean
  /** The game log's filters (player, event kind, sort), applied to every log here. */
  logFilters?: ActivityFilterState
  /** Wide layouts: the log grown up the side column (the turn controls fold
   *  away to the top bar). Other layouts grow it in a sheet instead. */
  logGrown?: boolean
  /** Your turn, chat / log open: a map tap (territory or background) returns
   *  to the turn controls. Pass only while a panel is open. */
  onTurnMapPress?: () => void
  renderTurnChatLog?: (args: {
    orientation: 'bottom-panel' | 'left-sidebar'
    topContent?: React.ReactNode
  }) => React.ReactNode
  /** Soft-keyboard top while inline chat is being typed into (portrait lift). */
  dockKeyboardTop?: number | null
  /** Wide layouts: top-bar-height strip over the map (see GameMapHost). */
  mapTopReserve?: number

  // Region details overlay
  regionDetailsMode: boolean
  focusedRegionId: string | null
  focusedDetailTerritoryId: string | null
  detailAdjacentIds: string[]
  regionColorById: Record<string, string>
  onDetailRegionPress: (regionId: string) => void
  onDetailTerritoryPress: (territoryId: string) => void
  /** Tap on the map background (ocean) in the regions view → clear selection. */
  onDetailBackgroundPress: () => void
  /** Bumped when the map Reset fires — sends the region list back to the top. */
  regionDetailsResetSignal: number

  // Corner overlays (chips)
  regionToggleNode: React.ReactNode
  regionToggleCorner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  zoomToggleNode: React.ReactNode
  zoomToggleCorner: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
  /** Thin control bar rendered beneath the map (zoom + view mode). Optional —
   * when omitted, the map keeps the corner-chip overlays. */
  mapControlsBar?: React.ReactNode

  // Zoom / pan
  zoomEnabled: boolean
  zoomScale: number
  panOffset: PanOffset
  onPanChange: (next: PanOffset) => void
  onZoomScaleChange: (scale: number) => void

  // Action panel header (landscape cog + roster row)
  landscapeActionHeader: React.ReactNode

  // Cards modal opener (used inside ReinforceUI/PostElimPlacementUI cardChip)
  onOpenCardsModal: () => void

  // WS connected-at ref value (passed to MapView viewerJoinedAt, epoch ms)
  viewerJoinedAt: number

  // Right overlay (always null per current design)
  mapRightOverlay: React.ReactNode

  // User
  viewerUserId: string | undefined
  onBootPlayer?: (userId: string, name: string) => void

  // Optional contextual help. When false, no additional node is rendered and
  // the live-game layout remains the normal game UI.
  tutorialEnabled?: boolean
  onTutorialEnabledChange?: (enabled: boolean) => void
}


/**
 * Wide layouts with persistent chat/log, your turn: the turn-controls area
 * keeps at least this much height reserved (so engaging or leaving an attack
 * or fortify doesn't bounce the chat/log below), grows with its content up to
 * TURN_AREA_MAX_FRACTION of the window (then scrolls), and chat + log fill the
 * rest directly beneath it.
 */
export const TURN_AREA_MIN_HEIGHT = 320
export const TURN_AREA_MAX_FRACTION = 0.6
/** Turn area opening (your turn) / closing (end turn). */
export const TURN_AREA_ANIMATION_MS = 350

export function ActiveGameView({
  game,
  gameState,
  myPlayer,
  myPlayerId,
  isMyTurn,
  slotByPlayerId,
  playerDisplayName,
  activePhaseView,
  placementStack,
  onPlacementStackChange,
  placementStepMode,
  onPlacementStepModeChange,
  isLandscape,
  isDesktop,
  historicalGameState,
  historicalEventIndex,
  inHistoricalView,
  onScrubEvent,
  onReturnToLive,
  snapshotArrowKeysEnabled = false,
  onViewAtEvent,
  chatBar,
  renderTurnChatLog,
  turnLogOpen = false,
  persistentChatLog = false,
  chatOnlyLog = false,
  logFilters,
  logGrown = false,
  onTurnMapPress,
  dockKeyboardTop = null,
  mapTopReserve = 0,
  regionDetailsMode,
  focusedRegionId,
  focusedDetailTerritoryId,
  detailAdjacentIds,
  regionColorById,
  onDetailRegionPress,
  onDetailTerritoryPress,
  onDetailBackgroundPress,
  regionDetailsResetSignal,
  regionToggleNode,
  regionToggleCorner,
  zoomToggleNode,
  zoomToggleCorner,
  mapControlsBar,
  zoomEnabled,
  zoomScale,
  panOffset,
  onPanChange,
  onZoomScaleChange,
  landscapeActionHeader,
  onOpenCardsModal,
  viewerJoinedAt,
  mapRightOverlay,
  viewerUserId,
  onBootPlayer,
  tutorialEnabled = false,
  onTutorialEnabledChange = () => {},
}: ActiveGameViewProps) {
  // The live feed renders the wire EVENT TAIL. To map a tapped row to its
  // absolute index in the full log (what the time-machine replays), the sidebar
  // adds this offset = total events − tail length. 0 when nothing was trimmed.
  const liveEventOffset = Math.max(
    0,
    (gameState.eventCount ?? 0) - (gameState.events?.length ?? 0),
  )

  // The regions list/detail sidebar. Built once and rendered by whichever view
  // is showing — the live region-details branch below, or the snapshot's
  // regions mode. One node, so the two can't drift.
  // ...but only ONE of them may be MOUNTED at a time. Rendering it in both
  // places left the hidden copy (the live branch is display:none in snapshot)
  // trying to scroll its expanded region into view, and measureLayout on a
  // display:none node logs "ref.measureLayout must be called with a ref to a
  // native component" every time a region is tapped. The live copy has nothing
  // to preserve across the toggle — its selection lives in the parent — so it
  // simply steps aside while the snapshot owns the panel.
  const regionDetailsPanelNode = (
    <RegionDetailsPanel
      state={gameState}
      gamePlayers={game.players}
      regionColors={regionColorById}
      selectedRegionId={focusedRegionId}
      onRegionPress={onDetailRegionPress}
      selectedTerritoryId={focusedDetailTerritoryId}
      onSelectTerritory={onDetailTerritoryPress}
      resetSignal={regionDetailsResetSignal}
    />
  )

  // One shared card projection drives both the placement chip and contextual
  // help so the two surfaces cannot disagree about whether a set is available.
  const viewerHand = gameState.cards?.[myPlayerId] ?? []
  const viewerHasTradeableCardSet = hasValidSet(viewerHand)
  // Mandatory status comes from the live hand size rather than a stale
  // persisted flag. Such hands open the cards modal automatically; a usable
  // 3–4 card set gets the steady placement indicator.
  const viewerMustTurnIn = isMyTurn && handForcesTurnIn(viewerHand.length)
  const cardChipNode = (
    <CardHudButton
      handCount={viewerHand.length}
      canTurnIn={viewerHasTradeableCardSet}
      mustTurnIn={viewerMustTurnIn}
      onPress={onOpenCardsModal}
    />
  )

  // All live branches share one MapView. Phase UIs register only the map
  // interaction projection (selection, valid targets, and press handler), so
  // changing from attack to fortify does not remount the SVG tree.
  const [mapRegistration, setMapRegistration] = React.useState<GameMapSceneRegistrationState | null>(null)
  const mapRegistrationRef = React.useRef<GameMapSceneRegistrationState | null>(null)
  const registerMapScene = React.useCallback<GameMapSceneRegistration>((key, config) => {
    // Keep the equality check outside React state as well. A phase registration
    // is driven by a layout effect; if a child re-runs that effect while the
    // parent is committing, relying only on the state updater can still enqueue
    // an avoidable render before React has observed the previous value.
    const previous = mapRegistrationRef.current
    const next = updateGameMapSceneRegistration(previous, key, config)
    if (next === previous) return
    mapRegistrationRef.current = next
    setMapRegistration(next)
  }, [])

  const regionMapConfig: GameMapSceneConfig = {
    showRegionBonuses: true,
    regionFocusId: focusedRegionId,
    selectedRegionId: focusedRegionId ?? undefined,
    selectedRegionColor: focusedRegionId ? regionColorById[focusedRegionId] : undefined,
    onTerritoryPress: onDetailTerritoryPress,
    onBackgroundPress: onDetailBackgroundPress,
    allTerritoriesInteractive: true,
    selectedTerritoryId: focusedDetailTerritoryId ?? undefined,
    adjacentTerritoryIds: detailAdjacentIds,
  }

  // Your turn: tapping the map while chat / log covers the turn controls puts
  // the controls back (and the tap still plays). Stable wrapper — the
  // registered handler is already stable, so the map doesn't re-render.
  const turnMapPressRef = React.useRef(onTurnMapPress)
  turnMapPressRef.current = onTurnMapPress
  const registeredPress = isMyTurn && mapRegistration?.key === activePhaseView
    ? mapRegistration.config.onTerritoryPress
    : undefined
  const turnAwarePress = React.useMemo(
    () => registeredPress
      ? (territoryId: string) => {
          turnMapPressRef.current?.()
          registeredPress(territoryId)
        }
      : undefined,
    [registeredPress],
  )

  // A tap on the map background (ocean) does the same while chat / log is
  // open; the background layer is only mounted while that's the case.
  const registeredBackgroundPress = isMyTurn && mapRegistration?.key === activePhaseView
    ? mapRegistration.config.onBackgroundPress
    : undefined
  const turnPanelOpen = !!onTurnMapPress
  const turnAwareBackgroundPress = React.useMemo(
    () => turnPanelOpen
      ? () => {
          turnMapPressRef.current?.()
          registeredBackgroundPress?.()
        }
      : registeredBackgroundPress,
    [turnPanelOpen, registeredBackgroundPress],
  )

  const activeMapConfig = activePhaseView === 'region-details'
    ? regionMapConfig
    : isMyTurn && mapRegistration?.key === activePhaseView
      ? { ...mapRegistration.config, onTerritoryPress: turnAwarePress, onBackgroundPress: turnAwareBackgroundPress }
      : {}

  const reducedMotion = useReducedMotion()
  // Persistent chat/log column: the turn area opens and closes with the turn.
  // End turn → it shrinks to the top bar and the log slides up into its
  // place; your turn → it opens back up (log slides down) and the turn
  // controls fade in. JS-driven (min/max height are layout props).
  const { height: windowHeight } = useWindowDimensions()
  const { theme } = useTheme()
  const turnAreaMaxRest = Math.round(windowHeight * TURN_AREA_MAX_FRACTION)
  const turnAreaMin = React.useRef(new Animated.Value(isMyTurn ? TURN_AREA_MIN_HEIGHT : 0)).current
  const turnAreaMax = React.useRef(new Animated.Value(isMyTurn ? turnAreaMaxRest : windowHeight)).current
  const turnControlsOpacity = React.useRef(new Animated.Value(1)).current
  const turnAreaHeight = React.useRef(0)
  const lastTurnState = React.useRef(isMyTurn)
  React.useEffect(() => {
    if (lastTurnState.current === isMyTurn) {
      // Not a turn change (e.g. a resize): keep the resting cap current.
      if (isMyTurn) turnAreaMax.setValue(turnAreaMaxRest)
      return
    }
    lastTurnState.current = isMyTurn
    if (!persistentChatLog) return
    const from = turnAreaHeight.current
    const settle = (to: { min: number; max: number; opacity: number }) => {
      if (reducedMotion) {
        turnAreaMin.setValue(to.min)
        turnAreaMax.setValue(to.max)
        turnControlsOpacity.setValue(to.opacity)
        return
      }
      const timing = (value: Animated.Value, toValue: number) => Animated.timing(value, {
        toValue, duration: TURN_AREA_ANIMATION_MS, easing: Easing.inOut(Easing.cubic), useNativeDriver: false,
      })
      Animated.parallel([
        timing(turnAreaMin, to.min),
        timing(turnAreaMax, to.max),
        timing(turnControlsOpacity, to.opacity),
      ]).start()
    }
    if (isMyTurn) {
      // Open from the top bar's height; controls fade in as it opens.
      turnAreaMin.setValue(from)
      turnAreaMax.setValue(from)
      turnControlsOpacity.setValue(0)
      settle({ min: TURN_AREA_MIN_HEIGHT, max: turnAreaMaxRest, opacity: 1 })
    } else {
      // Close from wherever the turn area was down to the top bar.
      turnAreaMin.setValue(from)
      turnAreaMax.setValue(windowHeight)
      settle({ min: 0, max: windowHeight, opacity: 1 })
    }
  }, [isMyTurn, persistentChatLog, reducedMotion, turnAreaMaxRest, windowHeight, turnAreaMin, turnAreaMax, turnControlsOpacity])
  // Grown log (wide layouts, your turn): the turn area folds to the top bar's
  // height (the phase panels carry the top bar first, so the clip leaves just
  // it) and the log slides up the column. The controls stay mounted, so a
  // staged move survives. Shrinking opens it back up.
  const lastGrown = React.useRef(logGrown)
  React.useEffect(() => {
    if (lastGrown.current === logGrown) return
    lastGrown.current = logGrown
    if (!persistentChatLog || !isMyTurn) return
    const to = logGrown
      ? { min: 0, max: GAME_TOP_BAR_HEIGHT, opacity: 1 }
      : { min: TURN_AREA_MIN_HEIGHT, max: turnAreaMaxRest, opacity: 1 }
    if (reducedMotion) {
      turnAreaMin.setValue(to.min)
      turnAreaMax.setValue(to.max)
      turnControlsOpacity.setValue(to.opacity)
      return
    }
    turnAreaMax.setValue(turnAreaHeight.current)
    const timing = (value: Animated.Value, toValue: number) => Animated.timing(value, {
      toValue, duration: TURN_AREA_ANIMATION_MS, easing: Easing.inOut(Easing.cubic), useNativeDriver: false,
    })
    Animated.parallel([
      timing(turnAreaMin, to.min),
      timing(turnAreaMax, to.max),
      timing(turnControlsOpacity, to.opacity),
    ]).start()
  }, [logGrown, isMyTurn, persistentChatLog, reducedMotion, turnAreaMaxRest, turnAreaMin, turnAreaMax, turnControlsOpacity])
  // Tips switched off (long-press popup or the game menu): no "tips are off"
  // notice. The tip on screen fades out, then leaves with a layout
  // animation so the bars and map ease into place instead of jumping.
  const lastTipCopy = React.useRef<TutorialHelpCopy | null>(null)
  const [leavingTipCopy, setLeavingTipCopy] = React.useState<TutorialHelpCopy | null>(null)
  const leavingTipOpacity = React.useRef(new Animated.Value(1)).current
  const [seenTutorialEnabled, setSeenTutorialEnabled] = React.useState(tutorialEnabled)
  if (seenTutorialEnabled !== tutorialEnabled) {
    setSeenTutorialEnabled(tutorialEnabled)
    setLeavingTipCopy(tutorialEnabled ? null : lastTipCopy.current)
  }
  React.useEffect(() => {
    if (!leavingTipCopy) return
    const finish = () => {
      configureTipExitLayout()
      setLeavingTipCopy(null)
    }
    leavingTipOpacity.setValue(1)
    if (reducedMotion) {
      finish()
      return
    }
    const fade = Animated.timing(leavingTipOpacity, {
      toValue: 0,
      duration: TIP_EXIT_FADE_MS,
      useNativeDriver: true,
    })
    fade.start(({ finished }) => { if (finished) finish() })
    return () => fade.stop()
  }, [leavingTipCopy, leavingTipOpacity, reducedMotion])

  const handleDisableTutorial = React.useCallback(() => {
    onTutorialEnabledChange(false)
  }, [onTutorialEnabledChange])

  const [tipsCollapsed, toggleTipsCollapsed] = useTutorialTipsCollapsed()
  const tutorialHelp = tutorialEnabled ? (() => {
    const isSelectionPhase = activePhaseView === 'attack' || activePhaseView === 'fortify'
    const registeredConfig = isSelectionPhase && mapRegistration?.key === activePhaseView
      ? mapRegistration.config
      : null
    const selectedFromId = registeredConfig?.selectedTerritoryId ?? null
    const selectedToId = registeredConfig?.secondaryTerritoryId ?? null
    const currentPlayerId = gameState.players[gameState.turnIndex]
    const currentPlayerName = currentPlayerId
      ? game.players.find((player) => String(player.slot) === currentPlayerId)?.displayName
      : null
    const pendingConquest = activePhaseView === 'attack' ? gameState.lastConquest : null
    const transferSource = pendingConquest
      ? gameState.map.territories.find((territory) => territory.id === pendingConquest.fromTerritoryId)
      : null
    const copy = tutorialHelpCopy({
      phase: activePhaseView,
      isMyTurn,
      selectedFromId,
      selectedToId,
      freeFortifyPending: !!pendingConquest && !!transferSource && transferSource.armies > 1,
      hasTradeableCardSet: viewerHasTradeableCardSet,
      continuousAttackEnabled: registeredConfig?.continuousAttackEnabled,
      currentPlayerName,
      teams: isTeamGame({ state: gameState }),
      targetIsTeammate: activePhaseView === 'attack' && isTeammateTerritory(gameState, myPlayerId,
        selectedToId ? gameState.map.territories.find((territory) => territory.id === selectedToId) : null),
    })
    lastTipCopy.current = copy
    return (
      <TutorialHelpStrip
        copy={copy}
        onDisable={handleDisableTutorial}
        edge={isLandscape ? 'bottom' : 'top'}
        collapsed={tipsCollapsed}
        onToggleCollapsed={toggleTipsCollapsed}
      />
    )
  })() : leavingTipCopy ? (
    <Animated.View pointerEvents="none" style={{ opacity: leavingTipOpacity }}>
      <TutorialHelpStrip
        copy={leavingTipCopy}
        onDisable={handleDisableTutorial}
        edge={isLandscape ? 'bottom' : 'top'}
        collapsed={tipsCollapsed}
        onToggleCollapsed={toggleTipsCollapsed}
      />
    </Animated.View>
  ) : null

  // Landscape/tablet keeps the action column on the left. Put contextual help
  // between its existing header and controls/dialog; portrait puts the strip
  // on top of the map controls bar (GameMapHost).
  // Desktop/landscape: the inline chat bar sits at the top of the left column,
  // under its header and above the log (or turn controls). Portrait puts it
  // under the map controls bar instead (GameMapHost).
  // Persistent chat/log (wide layouts) puts the chat bar in its own fixed
  // section instead, so the column header carries only the top bar + tips.
  const landscapeChatBar = isLandscape ? chatBar : undefined
  const headerChatBar = persistentChatLog ? undefined : landscapeChatBar
  const bootNotice = onBootPlayer ? <BootOverdueNotice gameId={game.id} state={gameState} players={game.players} viewerUserId={viewerUserId} bootableAt={game.bootableAt} bootAfterSeconds={game.bootAfterSeconds} onBootPlayer={onBootPlayer} /> : null
  // Phone web: a new build shows here, in the tip slot, rather than as a
  // toast over the map (components/game/WebUpdateStrip).
  const mapNotices = <>{bootNotice}<WebUpdateStrip edge={isLandscape ? 'bottom' : 'top'} />{tutorialHelp}</>
  const landscapeActionPanelHeader = isLandscape
    ? <>{landscapeActionHeader}{mapNotices}{headerChatBar}</>
    : landscapeActionHeader

  const activeActionPanel = (() => {
    if (activePhaseView === 'region-details') {
      // Landscape/desktop: the top bar (menu, back, players) lives in the
      // column header, so the regions list must keep it above itself rather
      // than replacing the whole column. Portrait has its own top bar.
      return isLandscape && landscapeActionHeader ? (
        // Same width and edge as the game-mode column, so toggling Game ↔
        // Regions never resizes the column or moves the map.
        <View testID="region-details-column" className="border-r border-olive"
          style={{ width: recentActivitySidebarWidth(isDesktop), flexShrink: 0 }}>
          {landscapeActionPanelHeader}
          <View style={{ flex: 1 }}>{regionDetailsPanelNode}</View>
        </View>
      ) : regionDetailsPanelNode
    }

    // The live activity log: the default view off-turn, and what the map
    // bar's log button swaps in for the turn controls on your turn.
    const activityLog = isLandscape ? (
      <RecentActivitySidebar
        gameId={game.id}
        state={gameState}
        players={game.players}
        topContent={landscapeActionPanelHeader}
        onViewAtEvent={onViewAtEvent}
        hideHeader
        selectedEventIndex={historicalEventIndex}
        eventIndexOffset={liveEventOffset}
        infiniteScroll
        filters={logFilters}
      />
    ) : (
      <RecentActivitySidebar
        gameId={game.id}
        state={gameState}
        players={game.players}
        orientation="bottom-panel"
        onViewAtEvent={onViewAtEvent}
        hideHeader
        selectedEventIndex={historicalEventIndex}
        eventIndexOffset={liveEventOffset}
        infiniteScroll
        filters={logFilters}
      />
    )

    // Wide layouts: the fixed chat + log section. It keeps ONE tree position
    // on and off turn (the second child of the column below), so a message
    // being typed when your turn starts keeps its input and keyboard.
    const columnWidth = recentActivitySidebarWidth(isDesktop)
    const persistentSection = persistentChatLog && isLandscape ? (
      <View
        testID="persistent-chat-log"
        style={{ flex: 1, flexDirection: 'row' }}
      >
        {chatOnlyLog && renderTurnChatLog ? renderTurnChatLog({ orientation: 'left-sidebar', topContent: landscapeChatBar }) : (
          <RecentActivitySidebar
            gameId={game.id}
            state={gameState}
            players={game.players}
            topContent={landscapeChatBar}
            onViewAtEvent={onViewAtEvent}
            hideHeader
            selectedEventIndex={historicalEventIndex}
            eventIndexOffset={liveEventOffset}
            infiniteScroll
            filters={logFilters}
          />
        )}
      </View>
    ) : null

    // Wide layouts: ONE column shape on and off turn — an animated top area
    // (the turn controls on your turn, just the top bar otherwise) over the
    // chat/log section. Same right rule and column width throughout, so the
    // divider is one line.
    const persistentColumn = (top: React.ReactNode) => (
      <View style={{ flexDirection: 'column' }}>
        {/* The rule sits on a plain View: a className on the Animated.View
            isn't applied on web, which left this part of the divider one
            pixel lighter than beside the log. */}
        <View className="border-r border-olive" style={{ width: columnWidth, flexShrink: 0 }}>
          <Animated.View
            testID="turn-controls-area"
            onLayout={(event) => { turnAreaHeight.current = event.nativeEvent.layout.height }}
            style={{ overflow: 'hidden', minHeight: turnAreaMin, maxHeight: turnAreaMax, flexDirection: 'row' }}
          >
            {top}
          </Animated.View>
        </View>
        {persistentSection}
      </View>
    )

    if (!isMyTurn) {
      if (!persistentSection) {
        // Off-turn with the input's chat-only filter on: just the chat.
        if (chatOnlyLog && renderTurnChatLog) {
          return renderTurnChatLog({
            orientation: isLandscape ? 'left-sidebar' : 'bottom-panel',
            topContent: isLandscape ? landscapeActionPanelHeader : undefined,
          })
        }
        return activityLog
      }
      return persistentColumn(<View style={{ flex: 1 }}>{landscapeActionPanelHeader}</View>)
    }

    // Your turn: chat, log, or neither (the turn controls) — exclusive.
    // (Wide layouts show both all the time instead — persistentSection.)
    const turnChatLog = persistentSection ? null : renderTurnChatLog?.({
      orientation: isLandscape ? 'left-sidebar' : 'bottom-panel',
      topContent: isLandscape ? landscapeActionPanelHeader : undefined,
    }) ?? (turnLogOpen ? activityLog : null)

    // Your turn: the phase UI is ALWAYS wrapped (stable tree position) so the
    // chat log can take over the area without unmounting staged turn state.
    const phasePanel = (() => {
    const commonPhaseProps = {
      gameId: game.id,
      state: gameState,
      myPlayerId,
      slotByPlayerId,
      gamePlayers: game.players,
      viewerUserId,
      mapRightOverlay,
      regionToggleNode: regionToggleNode,
      regionToggleCorner: regionToggleCorner,
      zoomToggleNode: zoomToggleNode,
      zoomToggleCorner: zoomToggleCorner,
      actionPanelHeader: landscapeActionPanelHeader,
      zoomEnabled,
      panOffset,
      onPanChange,
      zoomScale,
      onZoomScaleChange,
      viewerJoinedAt,
      onViewAtEvent,
      selectedEventIndex: historicalEventIndex,
      mapControlsBar,
      mapSceneKey: activePhaseView,
      onMapSceneConfigChange: registerMapScene,
      // Persistent chat/log column: panels size to their content.
      fitContent: !!persistentSection,
    }

    if (activePhaseView === 'claim') {
      return <ClaimUI {...commonPhaseProps} />
    }
    if (activePhaseView === 'placement') {
      return (
        <ReinforceUI
          {...commonPhaseProps}
          cardChip={cardChipNode}
          placementStack={placementStack}
          onPlacementStackChange={onPlacementStackChange}
          stepMode={placementStepMode}
          onStepModeChange={onPlacementStepModeChange}
        />
      )
    }
    if (activePhaseView === 'post-elim-placement') {
      return (
        <PostElimPlacementUI
          {...commonPhaseProps}
          cardChip={cardChipNode}
          placementStack={placementStack}
          onPlacementStackChange={onPlacementStackChange}
          stepMode={placementStepMode}
          onStepModeChange={onPlacementStepModeChange}
        />
      )
    }
    if (activePhaseView === 'attack') {
      return (
        <AttackUI
          {...commonPhaseProps}
          playerDisplayName={playerDisplayName}
          events={gameState.events}
        />
      )
    }
    return <FortifyUI {...commonPhaseProps} events={gameState.events} />
    })()
    if (persistentSection) {
      // Turn controls in a reserved-height area that grows with its content
      // (capped, then scrolls); chat + log directly beneath. The controls
      // fade in when your turn opens the area.
      return persistentColumn(
        <Animated.View testID="turn-phase-panel" style={{ flex: 1, flexDirection: 'row', opacity: turnControlsOpacity, backgroundColor: TURN_PANEL_COLORS[theme].background }}>
          {phasePanel}
        </Animated.View>,
      )
    }
    return (
      <>
        <View
          testID={turnChatLog ? 'turn-controls-hidden' : 'turn-phase-panel'}
          style={turnChatLog ? { display: 'none' } : { ...(isLandscape ? { flexDirection: 'row' as const } : { flex: 1 }), backgroundColor: TURN_PANEL_COLORS[theme].background }}
        >
          {phasePanel}
        </View>
        {turnChatLog}
      </>
    )
  })()

  // Pair the SVG-only marker in MapView with a sampled marker around the live
  // game scene. If the outer commit is slow while the SVG is not, the next
  // optimization belongs in the phase HUD/roster/activity surfaces rather
  // than the renderer. Keep this sampled, slow-only, and content-free.
  const gameSceneSampledRef = React.useRef(shouldSampleMapRender())
  const lastGameSceneReportRef = React.useRef(0)
  const onGameSceneRender = React.useCallback<React.ProfilerOnRenderCallback>(
    (_id, phase, actualDuration) => {
      if (!shouldReportMapRender(actualDuration, gameSceneSampledRef.current)) return
      const now = Date.now()
      if (now - lastGameSceneReportRef.current < MAP_RENDER_REPORT_COOLDOWN_MS) return
      lastGameSceneReportRef.current = now
      const device = collectDeviceInfo()
      reportMessage('slow-active-game-render', {
        level: 'info',
        tags: { mapId: gameState.map.id, platform: String(device.platform ?? 'unknown') },
        extra: {
          phase,
          actualDurationMs: Math.round(actualDuration * 10) / 10,
          deviceModel: device.deviceModel ?? 'unknown',
        },
      })
    },
    [gameState.map.id],
  )

  return (
    // Time-machine sits OVER the live layout (rendered as a sibling,
    // visible only when inHistoricalView). Keeping the live phase UI
    // mounted underneath preserves the player's in-progress state
    // (e.g. staged reinforcement placements) across snapshot
    // toggles — when they tap "Back to live", their pending
    // placements are still there.
    <React.Profiler id={`active-game-${game.id}`} onRender={onGameSceneRender}>
    <TurnPanelDesktopContext.Provider value={isDesktop}>
    <View className="flex-1">
      <View className="flex-1" style={inHistoricalView ? { display: 'none' } : undefined}>
        <GameMapHost
          visible={!inHistoricalView}
          state={gameState}
          viewerSlot={myPlayer?.slot ?? 1}
          isLandscape={isLandscape}
          isDesktop={isDesktop}
          mapConfig={activeMapConfig}
          actionPanel={activeActionPanel}
          mapControlsBar={mapControlsBar}
          tutorialHelp={isLandscape ? undefined : mapNotices}
          mapRightOverlay={mapRightOverlay}
          regionToggleNode={regionToggleNode}
          regionToggleCorner={regionToggleCorner}
          zoomToggleNode={zoomToggleNode}
          zoomToggleCorner={zoomToggleCorner}
          zoomEnabled={zoomEnabled}
          zoomScale={zoomScale}
          panOffset={panOffset}
          onPanChange={onPanChange}
          onZoomScaleChange={onZoomScaleChange}
          viewerJoinedAt={viewerJoinedAt}
          chatBar={isLandscape ? undefined : chatBar}
          dockKeyboardTop={dockKeyboardTop}
          landscapeTopReserve={mapTopReserve}
        />
      </View>
      {historicalGameState && (
        <View className="flex-1" style={inHistoricalView ? undefined : { display: 'none' }}>
          <HistoricalActiveView
            id={game.id}
            historicalGameState={historicalGameState}
            historicalEventIndex={historicalEventIndex as number}
            totalEvents={gameState.eventCount ?? 0}
            game={game}
            myPlayer={myPlayer}
            isLandscape={isLandscape}
            isDesktop={isDesktop}
            onScrubEvent={onScrubEvent}
            onReturnToLive={onReturnToLive}
            arrowKeysEnabled={snapshotArrowKeysEnabled}
            regionToggleNode={regionToggleNode}
            regionToggleCorner={regionToggleCorner}
            zoomToggleNode={zoomToggleNode}
            zoomToggleCorner={zoomToggleCorner}
            mapControlsBar={mapControlsBar}
            zoomEnabled={zoomEnabled}
            panOffset={panOffset}
            onPanChange={onPanChange}
            zoomScale={zoomScale}
            onZoomScaleChange={onZoomScaleChange}
            showRegionBonuses={regionDetailsMode}
            // Regions mode in the snapshot is the FULL inspector, not just
            // bonus badges over a frozen board: the log steps aside for the
            // region list and the map becomes tappable. Same selection state
            // the live regions view uses, so toggling between live and
            // snapshot keeps whatever region is focused.
            regionsMode={
              regionDetailsMode
                ? {
                    panel: regionDetailsPanelNode,
                    focusedRegionId,
                    focusedDetailTerritoryId,
                    detailAdjacentIds,
                    regionColorById,
                    onTerritoryPress: onDetailTerritoryPress,
                    onBackgroundPress: onDetailBackgroundPress,
                  }
                : null
            }
          />
        </View>
      )}
    </View>
    </TurnPanelDesktopContext.Provider>
    </React.Profiler>
  )
}
